/**
 * LocalStorage persistence for the whole Lunchly document.
 *
 * Everything the manager has done - settings, staff rules, aliases, imported
 * rotas and generated schedules - lives in one JSON blob under a single key.
 * One key keeps a save atomic (there is no half-written document if the tab is
 * closed mid-write) and it lets `index.html` read the theme synchronously
 * before first paint. That pre-paint script is the reason `theme` sits at the
 * top level of the state instead of inside `settings` or `ui`: it must be
 * readable with a single `JSON.parse(raw).theme` and no module loading.
 *
 * Two rules govern this module:
 *
 *  1. **Nothing here throws.** Browser storage fails for reasons the user never
 *     sees - private browsing, an exhausted quota, a locked-down corporate
 *     profile. Lunchly has to keep working in memory when persistence is
 *     broken, so every entry point returns a value or an `{ ok, error }`
 *     result instead of propagating an exception into the render path.
 *  2. **A stored payload is untrusted input.** It may come from an older
 *     build, a hand-edited backup or a truncated write. `loadAppState` repairs
 *     field by field over a fresh default state rather than trusting the shape
 *     it finds, so one bad sub-object can never take the app down.
 *
 * Saving is debounced by the store, not here - this module writes immediately
 * when it is asked to.
 */

import { DAY_KEYS, mondayOf, toIsoDate } from '../core/time.js';
import { createDefaultSettings, normaliseSettings } from '../core/settings.js';
import {
  createAliasBook,
  createAssignment,
  createSchedule,
  createStaffRule,
  normaliseRota,
} from '../core/model.js';
import { buildSeedAliasBook, buildSeedStaffRules } from '../data/defaults.js';

/**
 * The one and only key Lunchly writes. Versioned in the name so a future
 * breaking change can ship alongside the old document instead of destroying
 * it. `index.html` hard-codes this same string - keep them in step.
 */
export const STORAGE_KEY = 'lunchly.state.v1';

/** Schema version of the AppState blob itself (not of Settings or Rota). */
export const APP_STATE_VERSION = 5;

/** Staff timing confirmations introduced with state v2. */
const V2_CONFIRMED_TIMING_RULE_IDS = new Set(['rachel-mckinley', 'rebekah-forde']);

/** Tuesday placement confirmation introduced with state v4. */
const V4_CONFIRMED_TIMING_RULE_IDS = new Set(['rona-bryson', 'josh-maunder', 'cathy-robinson']);

/**
 * Rules whose clock times were confined to a shift pattern in state v5.
 *
 * Both were written about a specific shift ("Rachel works 09:00-21:00 on
 * Monday…") but were being applied to every shift the person worked. A saved
 * copy has no `appliesToShifts`, and a saved rule shadows the corrected seed, so
 * without this migration the fix reaches nobody who has used Lunchly before.
 */
const V5_SHIFT_SCOPED_RULE_IDS = new Set(['rachel-mckinley', 'rebekah-forde']);

/** Marker written into backup files so an import can recognise its own format. */
const BACKUP_FORMAT = 'lunchly-backup';

/** Key used by the availability probe; removed immediately after writing. */
const PROBE_KEY = `${STORAGE_KEY}.probe`;

const THEMES = new Set(['light', 'dark', 'system']);
const VIEWS = new Set(['week', 'timeline', 'rota']);
const DAY_KEY_SET = new Set(DAY_KEYS);

/** Rota and schedule maps are keyed by the week-commencing ISO date. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Discord export defaults; mirrors the option table in docs/ARCHITECTURE.md §4.4. */
const DEFAULT_EXPORT_OPTIONS = Object.freeze({
  brackets: true,
  cloudEmoji: true,
  header: true,
  joinWith: ' & ',
  includeEmptyDays: false,
});

const UNAVAILABLE_MESSAGE =
  'Browser storage is unavailable - it may be disabled, or you may be browsing privately. '
  + 'Lunchly will keep working, but nothing will be remembered after you close this tab.';

const QUOTA_MESSAGE =
  'Browser storage is full, so this change was not saved. Export a backup, then delete a '
  + 'saved week or clear other site data to free some space.';

/* -------------------------------------------------------------------------- */
/* Availability                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Can we actually persist anything?
 *
 * Feature-detecting `window.localStorage` is not enough: Safari in private
 * mode and several enterprise policies expose the object but throw on the
 * first write, and simply reading the property throws inside a sandboxed
 * iframe. The only honest test is a real write followed by a real delete,
 * which is what this does.
 */
export function isStorageAvailable() {
  const storage = getStorage();
  if (!storage) return false;
  try {
    storage.setItem(PROBE_KEY, 'probe');
    storage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Defaults                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A brand-new document for a manager who has never used Lunchly.
 *
 * The default week is the Monday of the current real week so the app opens on
 * something meaningful with no setup; this is the only place in the codebase
 * that is allowed to read the system clock for a calendar date, because every
 * other module must stay deterministic under test.
 */
export function createDefaultAppState() {
  return {
    version: APP_STATE_VERSION,
    /** 'system' defers to the OS until the manager makes an explicit choice. */
    theme: 'system',
    weekCommencing: mondayOf(toIsoDate(new Date())),
    settings: createDefaultSettings(),
    staffRules: buildSeedStaffRules(),
    aliasBook: buildSeedAliasBook(),
    rotas: {},
    schedules: {},
    exportOptions: { ...DEFAULT_EXPORT_OPTIONS },
    ui: { view: 'week', activeDay: null },
  };
}

/* -------------------------------------------------------------------------- */
/* Load / save / reset                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Read the saved document, repairing whatever it finds.
 *
 * This runs during bootstrap, before any UI exists to show an error, so it can
 * never throw and never returns a partial object: a missing key, unreadable
 * storage, invalid JSON, a payload that is an array or a string, and a payload
 * missing half its sub-objects all resolve to a usable AppState. Anything
 * unexpected is logged as a warning so a corrupt save is still diagnosable.
 */
export function loadAppState() {
  const defaults = createDefaultAppState();
  const storage = getStorage();
  if (!storage) return defaults;

  let raw;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[lunchly] could not read saved state; starting from defaults', error);
    return defaults;
  }
  if (typeof raw !== 'string' || raw.trim() === '') return defaults;

  const parsed = tryParseJson(raw);
  if (!parsed.ok) {
    console.warn(`[lunchly] saved state was not valid JSON (${parsed.error}); starting from defaults`);
    return defaults;
  }
  if (!isPlainObject(parsed.value)) {
    console.warn('[lunchly] saved state was not an object; starting from defaults');
    return defaults;
  }
  if (Number(parsed.value.version) > APP_STATE_VERSION) {
    console.warn(
      `[lunchly] saved state is version ${parsed.value.version}, newer than this build understands `
      + `(v${APP_STATE_VERSION}); unknown fields will be dropped`,
    );
  }

  return repairAppState(parsed.value, defaults);
}

/**
 * Write the document.
 *
 * Returns a result object rather than throwing because the caller is a store
 * subscriber: a failed save must be surfaced as a "not saved" badge in the
 * topbar, not as an exception that aborts the render. A full quota gets its
 * own message since the recovery ("export a backup, delete a week") is
 * completely different from every other failure.
 *
 * @param {object} state - the AppState to persist.
 * @returns {{ ok: boolean, error?: string }}
 */
export function saveAppState(state) {
  const storage = getStorage();
  if (!storage) return { ok: false, error: UNAVAILABLE_MESSAGE };

  try {
    // The rota and its plan are this session's work, not saved configuration:
    // `repairAppState` deliberately starts every visit empty, so writing them
    // would only bloat the blob towards the storage quota for no benefit.
    const { rotas: _rotas, schedules: _schedules, ...persistable } = state ?? {};

    // JSON.stringify is inside the try as well: a circular reference smuggled
    // into the state by a component bug would otherwise escape as a throw.
    storage.setItem(STORAGE_KEY, JSON.stringify(persistable));
    return { ok: true };
  } catch (error) {
    if (isQuotaExceeded(error)) return { ok: false, error: QUOTA_MESSAGE };
    return { ok: false, error: `Lunchly could not save your work: ${describeError(error)}` };
  }
}

/**
 * Forget everything and hand back a fresh document.
 *
 * The key is removed rather than overwritten with defaults so that a reset
 * followed by a crash still leaves clean storage behind; the store persists
 * the returned state again on the next change.
 */
export function resetAppState() {
  const storage = getStorage();
  if (storage) {
    try {
      storage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[lunchly] could not clear saved state', error);
    }
  }
  return createDefaultAppState();
}

/* -------------------------------------------------------------------------- */
/* Backup / restore                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Serialise the document as a human-readable backup file.
 *
 * Indented rather than minified because managers do open these in a text
 * editor, and because a readable diff is the only way to audit what changed
 * between two weeks. The `_format` / `_exportedAt` metadata sits alongside the
 * state fields (underscore-prefixed to keep it out of the AppState namespace)
 * so the file is simultaneously a tagged backup and a valid bare state.
 *
 * @param {object} state - the AppState to export.
 * @returns {string} pretty JSON.
 */
export function exportBackup(state) {
  // A backup is the one place the week's work is meant to be kept, so it is
  // written with `restoreWork` on. Without it the file would come back holding
  // settings and nothing else - a backup that silently backs up almost nothing.
  const payload = {
    _format: BACKUP_FORMAT,
    _exportedAt: new Date().toISOString(),
    ...repairAppState(state, createDefaultAppState(), { restoreWork: true }),
  };
  return JSON.stringify(payload, null, 2);
}

/**
 * Restore a document from a backup file.
 *
 * Accepts the wrapper produced by {@link exportBackup} or a bare AppState,
 * because the file people actually hand each other is often a copy-pasted
 * fragment. Every failure is reported as a sentence a manager can act on - 
 * this is driven by a file picker, so "unexpected token < in JSON" alone would
 * leave them with no idea that they picked the wrong file.
 *
 * The restored state is *returned*, never written: applying it is the
 * caller's decision, so an import can be previewed or cancelled.
 *
 * @param {string|object} json - backup text, or an already-parsed payload.
 * @returns {{ ok: boolean, state?: object, error?: string }}
 */
export function importBackup(json) {
  let payload = json;

  if (typeof payload === 'string') {
    if (payload.trim() === '') {
      return { ok: false, error: 'That file is empty, so there is nothing to restore.' };
    }
    const parsed = tryParseJson(payload);
    if (!parsed.ok) {
      return { ok: false, error: `That file is not valid JSON (${parsed.error}).` };
    }
    payload = parsed.value;
  }

  if (Array.isArray(payload)) {
    return { ok: false, error: 'That file contains a list, not a Lunchly backup.' };
  }
  if (!isPlainObject(payload)) {
    return { ok: false, error: 'That file does not contain a Lunchly backup.' };
  }

  // Tolerate a nested `{ state: … }` wrapper as well as the flat export shape.
  const candidate = isPlainObject(payload.state) ? payload.state : payload;

  if (candidate.version === undefined || candidate.version === null) {
    return {
      ok: false,
      error: 'That file has no version number, so Lunchly cannot tell what it is.',
    };
  }

  const version = Number(candidate.version);
  if (!Number.isInteger(version) || version < 1) {
    return { ok: false, error: `Unrecognised backup version "${candidate.version}".` };
  }
  if (version > APP_STATE_VERSION) {
    return {
      ok: false,
      error: `That backup was made by a newer version of Lunchly (v${version}). `
        + `This copy understands up to v${APP_STATE_VERSION} - please update and try again.`,
    };
  }

  return {
    ok: true,
    state: repairAppState(candidate, createDefaultAppState(), { restoreWork: true }),
  };
}

/* -------------------------------------------------------------------------- */
/* Repair                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Merge an untrusted payload over a fresh default state, one field at a time.
 * Anything unrecognised is dropped instead of being carried forward, so the
 * result always matches the AppState contract exactly.
 */
/**
 * @param {object} stored The untrusted payload.
 * @param {object} defaults A fresh AppState to repair on top of.
 * @param {{ restoreWork?: boolean }} [options] `restoreWork` brings back the
 *   rotas and generated plans. False for an ordinary page load, true when the
 *   manager is explicitly restoring a backup.
 */
function repairAppState(stored, defaults, { restoreWork = false } = {}) {
  if (!isPlainObject(stored)) return defaults;

  /*
   * On an ordinary load the rota and its plan are deliberately NOT restored.
   *
   * A supervisor opens Lunchly to plan a specific week, and reopening it to last
   * week's rota - still showing last week's dates - is confusing at best and
   * wrong at worst. Every visit therefore starts empty and waits for an upload.
   *
   * Everything representing a lasting decision does persist: settings, staff
   * rules, aliases, export preferences and the theme. Those are configuration,
   * not this week's work.
   *
   * Restoring a backup is the opposite case. The manager exported that file on
   * purpose and expects the week inside it back, so `restoreWork` returns the
   * rotas, the schedules and the week they belong to.
   */
  const rotas = restoreWork ? repairRotas(stored.rotas) : {};
  const schedules = restoreWork ? repairSchedules(stored.schedules) : {};
  const weekCommencing = restoreWork && isIsoDate(stored.weekCommencing)
    ? (mondayOf(stored.weekCommencing) || defaults.weekCommencing)
    : defaults.weekCommencing;

  return {
    version: APP_STATE_VERSION,
    theme: THEMES.has(stored.theme) ? stored.theme : defaults.theme,
    weekCommencing,
    settings: normaliseSettings(stored.settings),
    staffRules: repairStaffRules(stored.staffRules, defaults.staffRules, Number(stored.version)),
    aliasBook: repairAliasBook(stored.aliasBook, defaults.aliasBook),
    rotas,
    schedules,
    exportOptions: repairExportOptions(stored.exportOptions),
    ui: repairUi(stored.ui, defaults.ui),
  };
}

/**
 * An absent rules array means "never saved" and restores the seeds; an empty
 * array means the manager deliberately deleted every rule and is respected.
 */
function repairStaffRules(value, fallback, sourceVersion) {
  if (!Array.isArray(value)) return fallback;
  const repaired = value
    .filter(isPlainObject)
    .map((rule) => createStaffRule(rule))
    .filter((rule) => Boolean(rule.employeeId));

  // Each confirmed seed is applied once when its version first reaches an old
  // browser. Later deletions and edits remain respected after that migration.
  if (!Number.isInteger(sourceVersion) || sourceVersion < 2) {
    for (const seed of fallback) {
      if (!V2_CONFIRMED_TIMING_RULE_IDS.has(seed.employeeId)) continue;
      const index = repaired.findIndex((rule) => rule.employeeId === seed.employeeId);
      if (index === -1) {
        repaired.push(seed);
        continue;
      }
      repaired[index] = createStaffRule({
        ...repaired[index],
        preferredLunchTime: seed.preferredLunchTime,
        earliestLunchTime: seed.earliestLunchTime,
        latestLunchTime: seed.latestLunchTime,
        notes: seed.notes,
      });
    }
  }

  if (!Number.isInteger(sourceVersion) || sourceVersion < 4) {
    for (const seed of fallback) {
      if (!V4_CONFIRMED_TIMING_RULE_IDS.has(seed.employeeId)) continue;
      const index = repaired.findIndex((rule) => rule.employeeId === seed.employeeId);
      if (index === -1) {
        repaired.push(seed);
      } else {
        repaired[index] = createStaffRule({
          ...repaired[index],
          preferredLunchTimesByDay: seed.preferredLunchTimesByDay,
          notes: seed.notes,
        });
      }
    }
  }

  if (!Number.isInteger(sourceVersion) || sourceVersion < 5) {
    for (const seed of fallback) {
      if (!V5_SHIFT_SCOPED_RULE_IDS.has(seed.employeeId)) continue;
      const index = repaired.findIndex((rule) => rule.employeeId === seed.employeeId);
      if (index === -1) {
        repaired.push(seed);
      } else {
        // Only the scope and its explanation are refreshed. Any time the manager
        // has since changed on this rule is theirs, and is left alone.
        repaired[index] = createStaffRule({
          ...repaired[index],
          appliesToShifts: seed.appliesToShifts,
          notes: seed.notes,
        });
      }
    }
  }

  return repaired;
}

function repairAliasBook(value, fallback) {
  if (!isPlainObject(value)) return fallback;

  const entries = {};
  if (isPlainObject(value.entries)) {
    for (const [employeeId, alias] of Object.entries(value.entries)) {
      if (typeof alias === 'string' && alias.trim() !== '') entries[employeeId] = alias;
    }
  }

  const unresolved = (Array.isArray(value.unresolved) ? value.unresolved : []).filter(
    (item) => typeof item === 'string' || (isPlainObject(item) && typeof item.token === 'string'),
  );

  const repaired = createAliasBook(entries, unresolved);

  // Confirmed after the original seed shipped. Apply the correction to saved
  // v1 state too, otherwise an existing browser would keep warning that Cags is
  // unresolved even though fresh installs know she is Caroline Bentley.
  repaired.entries['caroline-bentley'] = 'Cags';
  repaired.unresolved = repaired.unresolved.map((item) => (
    String(item.token).toLowerCase() === 'cags'
      ? { ...item, resolvedTo: 'caroline-bentley', note: 'Confirmed by Josh: Cags is Caroline Bentley.' }
      : item
  ));
  return repaired;
}

/**
 * Rotas and schedules are keyed by week-commencing date. A key that is not a
 * `YYYY-MM-DD` string cannot be addressed by the week picker, so it would be
 * invisible dead weight in every future save - drop it.
 *
 * Only reached when restoring a backup; an ordinary page load starts empty.
 */
function repairRotas(value) {
  const rotas = {};
  if (!isPlainObject(value)) return rotas;

  for (const [weekCommencing, rota] of Object.entries(value)) {
    if (!isIsoDate(weekCommencing) || !isPlainObject(rota)) continue;
    rotas[weekCommencing] = normaliseRota({
      ...rota,
      // The map key is authoritative: a rota filed under the wrong week would
      // otherwise render with the wrong dates on every day card.
      weekCommencing,
      rows: Array.isArray(rota.rows) ? rota.rows.filter(isPlainObject) : [],
    });
  }
  return rotas;
}

function repairSchedules(value) {
  const schedules = {};
  if (!isPlainObject(value)) return schedules;

  for (const [weekCommencing, schedule] of Object.entries(value)) {
    if (!isIsoDate(weekCommencing) || !isPlainObject(schedule)) continue;
    schedules[weekCommencing] = repairSchedule(weekCommencing, schedule);
  }
  return schedules;
}

/**
 * Rebuild a schedule on top of a fresh shell so every day key exists with the
 * right date, then copy across only the parts that still look like data. A
 * half-written schedule therefore loses assignments rather than crashing the
 * week view on a missing `days.thu`.
 */
function repairSchedule(weekCommencing, stored) {
  const schedule = createSchedule(weekCommencing);
  schedule.rulesVersion = Number.isInteger(Number(stored.rulesVersion))
    ? Math.max(0, Number(stored.rulesVersion))
    : 0;
  schedule.generatedAt = typeof stored.generatedAt === 'string' ? stored.generatedAt : null;
  schedule.warnings = copyObjects(stored.warnings);
  schedule.stats = isPlainObject(stored.stats) ? { ...stored.stats } : null;

  const storedDays = isPlainObject(stored.days) ? stored.days : {};
  for (const dayKey of DAY_KEYS) {
    const day = storedDays[dayKey];
    if (!isPlainObject(day)) continue;
    schedule.days[dayKey] = {
      ...schedule.days[dayKey],
      assignments: copyObjects(day.assignments)
        .filter((assignment) => typeof assignment.employeeId === 'string' && assignment.employeeId)
        .map((assignment) =>
          createAssignment({
            ...assignment,
            // The containing day is authoritative; a mislabelled assignment
            // would otherwise be edited on one card and rendered on another.
            dayKey,
            acknowledgedWarnings: Array.isArray(assignment.acknowledgedWarnings)
              ? [...assignment.acknowledgedWarnings]
              : [],
          }),
        ),
      unassigned: copyObjects(day.unassigned),
      coverage: copyObjects(day.coverage),
      warnings: copyObjects(day.warnings),
      solved: day.solved !== false,
    };
  }
  return schedule;
}

function repairExportOptions(value) {
  const options = { ...DEFAULT_EXPORT_OPTIONS };
  if (!isPlainObject(value)) return options;

  for (const key of Object.keys(DEFAULT_EXPORT_OPTIONS)) {
    const stored = value[key];
    // `joinWith` is the only free-text option; the empty string is a legitimate
    // choice ("run the names together"), so only the type is checked.
    if (key === 'joinWith') {
      if (typeof stored === 'string') options.joinWith = stored;
    } else if (typeof stored === 'boolean') {
      options[key] = stored;
    }
  }
  return options;
}

function repairUi(value, fallback) {
  if (!isPlainObject(value)) return { ...fallback };
  return {
    view: VIEWS.has(value.view) ? value.view : fallback.view,
    activeDay: DAY_KEY_SET.has(value.activeDay) ? value.activeDay : null,
  };
}

/* -------------------------------------------------------------------------- */
/* Low-level helpers                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Reading `globalThis.localStorage` is itself a throwing operation in a
 * sandboxed iframe and is simply undefined under node, so every access to it
 * in this module funnels through here.
 */
function getStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Storage quota errors are reported four different ways across browsers:
 * the modern DOMException name, Firefox's own name, the legacy numeric code 22
 * and Firefox's legacy code 1014. Recognising all four is what lets the UI
 * offer "export a backup and delete a week" instead of a generic failure.
 */
function isQuotaExceeded(error) {
  if (!error || typeof error !== 'object') return false;
  return (
    error.name === 'QuotaExceededError'
    || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || error.code === 22
    || error.code === 1014
  );
}

function tryParseJson(text) {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (error) {
    return { ok: false, error: describeError(error) };
  }
}

function describeError(error) {
  if (error && typeof error.message === 'string' && error.message) return error.message;
  return String(error);
}

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isIsoDate(value) {
  return typeof value === 'string' && ISO_DATE_RE.test(value);
}

/** Keep only the object entries of a maybe-array, shallow-copied so that the
 * restored state never shares mutable references with the payload it came from. */
function copyObjects(value) {
  if (!Array.isArray(value)) return [];
  return value.filter(isPlainObject).map((item) => ({ ...item }));
}
